import { DatePicker } from "components/DatePicker/DatePicker";
import {
  type EmailType,
  MultipleEmailInput,
} from "components/MultipleEmailInput/MultipleEmailInput";
import { format } from "date-fns";
import { Formik, type FormikHelpers } from "formik";
import { useAdmin } from "hooks/useAdmin";
import { useUserForm } from "hooks/useUserForm";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { Heading3 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { EducationItem } from "../../../../components/EducationItem/EducationItem";

import {
  FormLabel,
  StyledButton,
  StyledEducationButton,
  StyledField,
  StyledForm,
  StyledSelect,
} from "../../UserToolsPage.styled";
import type { DownloadInvitesReportFormFields } from "./DownloadInvitesReport.schema";

export const DownloadInvitesReport: React.FC = observer(() => {
  const { t } = useTranslation("admin-environment");
  const { downloadInvitedStudentsReport, downloadTeacherInvitesCountReport } = useAdmin();
  const { getEducations, schools } = useUserForm();
  const [emails, setEmails] = useState<EmailType[]>([]);

  const emailsArr = emails.map((x) => x.email);
  const formatDate = (date: Date): string => {
    return format(date, "yyyy-MM-dd");
  };
  // Call endpoint to transfer test upon submit
  const onFormSubmit = (
    { schoolId, educationIds, minSentDate, maxSentDate }: DownloadInvitesReportFormFields,
    { resetForm, setSubmitting }: FormikHelpers<DownloadInvitesReportFormFields>,
  ) => {
    setSubmitting(true);
    downloadInvitedStudentsReport(
      schoolId,
      educationIds,
      formatDate(minSentDate),
      formatDate(maxSentDate),
      emailsArr,
    ).finally(() => {
      setSubmitting(false);
      resetForm();
    });
  };
  const today = new Date();
  return (
    <Formik
      enableReinitialize
      initialValues={{
        schoolId: "",
        educationIds: [""],
        minSentDate: today,
        maxSentDate: today,
      }}
      onSubmit={onFormSubmit}
    >
      {(formik) => (
        <StyledForm
          noValidate
          data-cy="download-invites-report-form"
          onSubmit={formik.handleSubmit}
        >
          <Heading3>{t("adminTools.downloadInvitesReport.title")}</Heading3>
          <ParagraphLarge>{t("adminTools.downloadInvitesReport.description")}</ParagraphLarge>
          <StyledField>
            <FormLabel>
              {t(
                "adminTools.downloadInvitesReport.dateInput.startDate",
                "Start date of the report",
              )}
            </FormLabel>
            <DatePicker
              dataCy="start-date-picker"
              value={formik.values.minSentDate}
              maxDate={today}
              minDate={null}
              onChange={(date) => formik.setFieldValue("minSentDate", date)}
            />
            <FormLabel>
              {t("adminTools.downloadInvitesReport.dateInput.endDate", "End date of the report")}
            </FormLabel>
            <DatePicker
              dataCy="end-date-picker"
              value={formik.values.maxSentDate}
              maxDate={today}
              minDate={null}
              onChange={(date) => formik.setFieldValue("maxSentDate", date)}
            />
          </StyledField>
          <StyledField>
            <label htmlFor="user-admin-form-school-selector">
              {t("accountDetails.school.label", "School")}
            </label>
            <StyledSelect
              aria-label={t("accountDetails.school.label", "School")}
              dataCy="account-school"
              id="invites-report-school-selector"
              name="invites-report-schoolId"
              options={schools}
              value={formik.values.schoolId}
              onChange={(event) => {
                formik.setFieldValue("schoolId", event.target.value);
                formik.setFieldValue("educationIds", [""]);
              }}
            />
          </StyledField>
          <StyledField>
            <label htmlFor="user-admin-form-education-selector">
              {t("accountDetails.education.label", "Education")}
            </label>
            {formik.values.educationIds.map((education, index) => (
              <EducationItem
                key={education}
                ariaLabel={`${t("accountDetails.education.label", "Education")} ${index + 1}`}
                disabled={!formik.values.schoolId}
                education={education}
                educationOptions={getEducations(
                  formik.values.schoolId,
                  formik.values.educationIds,
                  education,
                )}
                errorMessage={t("admin-environment:accountDetails.educations.errors.required")}
                hasError={!!formik.errors?.educationIds}
                id={`user-admin-form-education-selector-${index}`}
                onDeleteEducation={() => {
                  const { values, setFieldValue } = formik;
                  if (values.educationIds.length > 1) {
                    setFieldValue(
                      "educationIds",
                      values.educationIds.filter(
                        (_value, educationIndex) => educationIndex !== index,
                      ),
                    );
                  } else {
                    setFieldValue("educationIds", [""]);
                  }
                }}
                onUpdateEducation={(updatedEducation) => {
                  const { values, setFieldValue } = formik;
                  const { educationIds } = values;
                  educationIds[index] = updatedEducation;
                  setFieldValue("educationIds", educationIds);
                }}
              />
            ))}
            <StyledEducationButton
              dataCy="add-education"
              disabled={
                formik.values.educationIds.length + 1 ===
                  getEducations(formik.values.schoolId).length ||
                !formik.values.schoolId ||
                formik.values.educationIds.some((education) => !education)
              }
              variant="default"
              onClick={(event) => {
                const { setFieldValue, values } = formik;
                event.preventDefault();
                setFieldValue("educationIds", [...values.educationIds, ""]);
              }}
            >
              {t("accountDetails.button.addEducation", "+ Add education")}
            </StyledEducationButton>
          </StyledField>
          <StyledField>
            <label htmlFor="user-admin-form-education-selector">
              {t("adminTools.downloadInvitesReport.teacherEmails", "Teacher emails")}
            </label>
            <MultipleEmailInput
              selectedEmails={emails}
              width="120%"
              onSelectedEmailsChanged={setEmails}
              isForTeachers
            />
          </StyledField>
          <StyledButton
            dataCy="download-invites-list-report-button"
            disabled={!!Object.keys(formik.errors).length || formik.isSubmitting}
            variant="primary"
            onClick={() => {
              formik.setSubmitting(true);
              downloadInvitedStudentsReport(
                formik.values.schoolId,
                formik.values.educationIds.filter((educationId) => !!educationId),
                formatDate(formik.values.minSentDate),
                formatDate(formik.values.maxSentDate),
                emailsArr.filter((email) => !!email),
              ).finally(() => {
                formik.setSubmitting(false);
              });
            }}
          >
            {t(
              "adminTools.downloadInvitesReport.buttons.downloadInvites.label",
              "Download invites report",
            )}
          </StyledButton>
          <StyledButton
            dataCy="download-invited-stats-count-report-button"
            disabled={!!Object.keys(formik.errors).length || formik.isSubmitting}
            variant="primary"
            onClick={() => {
              formik.setSubmitting(true);
              downloadTeacherInvitesCountReport(
                formik.values.schoolId,
                formik.values.educationIds.filter((educationId) => !!educationId),
                formatDate(formik.values.minSentDate),
                formatDate(formik.values.maxSentDate),
                emailsArr.filter((email) => !!email),
              ).finally(() => {
                formik.setSubmitting(false);
              });
            }}
          >
            {t(
              "adminTools.downloadInvitesReport.buttons.downloadPurchases.label",
              "Download central purchases report",
            )}
          </StyledButton>
          <StyledButton
            dataCy="clear-invites-report-button"
            variant="secondary"
            onClick={(event) => {
              event.preventDefault();
              formik.resetForm();
            }}
          >
            {t("adminTools.downloadInvitesReport.buttons.reset.label", "Reset form")}
          </StyledButton>
        </StyledForm>
      )}
    </Formik>
  );
});
